import { router } from '@inertiajs/vue3'
import { adminConsumerHeroSectionItems } from '@admin/api'
import type { AdminConsumerHeroSectionItem } from '@admin/serializers'
import type { AdminConsumerHeroSectionIndex } from '@admin/types/serializers'
import { asyncOpenDialog, confirmDeletion } from '@corp/services/dialogs'
import { showNotice } from '@corp/services/flash'

function openItemEditDialog ({
  heroSectionId,
  sectionItem,
  heroSection,
}:
{
  heroSectionId: number,
  sectionItem?: AdminConsumerHeroSectionItem,
  heroSection?: AdminConsumerHeroSectionIndex,
},
options: { onSaved?: () => void } = {},
) {
  return asyncOpenDialog(import('@admin/dialogs/heroSectionItems/EditHeroSectionItem.vue'), {
    sectionItem,
    heroSectionId,
    heroSection,
    // `create` answers with JSON rather than redirecting, so the caller has to
    // refresh its own list.
    onCreated () {
      showNotice('Slide added successfully.')
      options.onSaved?.()
    },
    onUpdated (updated: AdminConsumerHeroSectionItem) {
      if (sectionItem) Object.assign(sectionItem, updated)
      showNotice(`${updated.heroImage.name}'s priority has been successfully updated.`)
    },
  })
}

export function openDuplicate (
  {
    sectionItem,
    heroSection,
  }: {
    // this can be undefined if we are going the duplicate route (as in we duplicate the hero section)
    sectionItem?: AdminConsumerHeroSectionItem;
    heroSection: AdminConsumerHeroSectionIndex;
  },
  options: { onCreated?: (newSection: AdminConsumerHeroSectionIndex) => void } = {},
) {
  // by not passing the heroSection object here we suppress the landing page warning in the modal
  return asyncOpenDialog(import('@admin/dialogs/heroSectionItems/EditHeroSectionItem.vue'), {
    cloneId: sectionItem?.id,
    cloneSectionId: sectionItem ? undefined : heroSection.id,
    heroSection,
    sectionItem,
    onCreated: options.onCreated ?? ((newSection: AdminConsumerHeroSectionIndex) => {
      router.visit(adminConsumerHeroSectionItems.index.path({ hero_section_id: newSection.id }))
    }),
  })
}

// Inline "Add existing hero image": appends a hero image that already exists as a new
// slide on this section, picking the image in the section-item dialog.
export function openAddExistingToSection ({
  heroSection,
  landingPageSectionItemId,
  sectionHeroImageIds,
}: {
  heroSection: AdminConsumerHeroSectionIndex;
  landingPageSectionItemId?: number;
  sectionHeroImageIds?: number[];
}) {
  const sectionShared = (heroSection.landingPages?.length ?? 0) > 1

  // Without a landingPageSectionItemId there is nothing to repoint at a clone, so
  // "duplicate for this page only" would leave an orphaned section behind — offer only
  // the shared add. Heroes rendered by section_key (the legacy home fallback and the
  // occasions pages) are the ones that land here; the condition can go once they are
  // backed by a LandingPageSectionItem.
  if (sectionShared && landingPageSectionItemId) {
    return asyncOpenDialog(import('@admin/dialogs/landingPages/DuplicateOrEditSharedDialog/DuplicateOrEditSharedDialog.vue'), {
      landingPages: heroSection.landingPages ?? [],
      moduleName: 'hero section',
      duplicateLabel: 'Duplicate & Add',
      sharedLabel: 'Add to Shared',
      description: 'Do you want to add the slide to the shared hero section? Or duplicate the section and add the slide to that new section?',
      onDuplicateAndSave: () => openExistingImageDialog({
        heroSection,
        cloneSectionId: heroSection.id,
        landingPageSectionItemId,
        sectionHeroImageIds,
      }),
      onSaveFromShared: () => openExistingImageDialog({ heroSection, sectionHeroImageIds }),
    })
  }

  return openExistingImageDialog({ heroSection, sectionHeroImageIds })
}

function openExistingImageDialog ({
  heroSection,
  cloneSectionId,
  landingPageSectionItemId,
  sectionHeroImageIds,
}: {
  heroSection: AdminConsumerHeroSectionIndex;
  cloneSectionId?: number;
  landingPageSectionItemId?: number;
  sectionHeroImageIds?: number[];
}) {
  return asyncOpenDialog(import('@admin/dialogs/heroSectionItems/EditHeroSectionItem.vue'), {
    heroSectionId: heroSection.id,
    heroSection,
    cloneSectionId,
    landingPageSectionItemId,
    sectionHeroImageIds,
    onCreated: () => {
      showNotice('Slide added to the section.')
      window.location.reload()
    },
  })
}

export function openEdit ({
  heroSectionId,
  sectionItem,
  heroSection,
}: {
  heroSectionId: number
  sectionItem?: AdminConsumerHeroSectionItem
  heroSection?: AdminConsumerHeroSectionIndex
}, options: { onSaved?: () => void } = {}) {
  if (heroSection && (heroSection.landingPages?.length ?? 0) > 1) {
    return asyncOpenDialog(import('@admin/dialogs/landingPages/DuplicateOrEditSharedDialog/DuplicateOrEditSharedDialog.vue'), {
      landingPages: heroSection.landingPages,
      moduleName: 'Hero Section Item',
      onDuplicateAndSave: () => openDuplicate({ sectionItem, heroSection }),
      onSaveFromShared: () => openItemEditDialog({
        heroSectionId,
        sectionItem,
        heroSection,
      }, options),
    })
  }
  return openItemEditDialog({ heroSectionId, sectionItem, heroSection }, options)
}

const removeMessage = (
  item: AdminConsumerHeroSectionItem & { sectionName: string },
) => <>
  <p><b>{item.heroImage.name}</b> will be removed for <b>{item.sectionName}</b>.</p>
  <p>This action cannot be reversed.</p>
</>

export function confirmRemove (
  item: AdminConsumerHeroSectionItem & { sectionName: string },
  heroSection?: AdminConsumerHeroSectionIndex,
  landingPageSectionItemId?: number,
) {
  // Delete without an Inertia visit (the controller redirects back, which would
  // misroute on the server-rendered inline editor), then reload so the carousel
  // reflects the removed slide.
  const destroyAndReload = () =>
    adminConsumerHeroSectionItems.destroy({ params: { id: item.id }, visit: false })
      .then(() => window.location.reload())

  // From the inline editor (landingPageSectionItemId present): clone the section
  // without this slide, repoint the current page at the clone, and reload in
  // place — consistent with the other inline actions. From the admin table
  // (no landingPageSectionItemId): keep navigating to the new section's index.
  const destroyAndDuplicate = () =>
    adminConsumerHeroSectionItems.destroyAndDuplicate({
      params: { id: item.id },
      data: { landing_page_section_item_id: landingPageSectionItemId },
      visit: false,
    }).then((result) => {
      showNotice('Section duplicated successfully.')
      if (landingPageSectionItemId) window.location.reload()
      else router.visit(adminConsumerHeroSectionItems.index.path({ hero_section_id: result.id }))
      return result
    })

  if ((heroSection?.landingPages?.length ?? 0) > 1) {
    return asyncOpenDialog(import('@admin/dialogs/DestroyDuplicateDialog/DestroyDuplicateDialog.vue'), {
      landingPages: heroSection!.landingPages,
      description: 'Choose how you want to remove this slide.',
      onClose: () => {},
      onDestroyShared: destroyAndReload,
      onDestroyDuplicate: destroyAndDuplicate,
    })
  }
  return confirmDeletion(item, {
    action: 'Remove Hero Section Item',
    message: removeMessage,
    onConfirm: destroyAndReload,
  })
}
